// frontend/src/pages/MieiOrdini.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import TornaAlMenu from "../components/TornaAlMenu";

const API_URL = process.env.REACT_APP_API_URL || "";

const isAgentRole = (role) => (role || "").toUpperCase() === "AGENTE";
const isOrderFromAgent = (o) => {
  const role =
    (o.sender_role ||
      o.sender?.role ||
      o.creator_role ||
      o.sender_role_name ||
      "").toString().toUpperCase();
  return role === "AGENTE";
};

const statoLabel = (s) => {
  const code = String(s || "").toUpperCase();
  if (code === "DRAFT") return "Bozza";
  if (code === "SUBMITTED" || code === "INVIATO") return "Inviato";
  if (code === "APPROVED") return "Approvato";
  if (code === "FULFILLED" || code === "EVASO") return "Evaso";
  if (code === "CANCELED" || code === "ANNULLATO") return "Annullato";
  return s || "—";
};

const appStatusLabel = (o) => {
  const code = String(o?.app_status || "").toUpperCase();
  if (code === "INVIATO") return "Ordine ricevuto";
  if (code === "IN_PREPARAZIONE") return "In preparazione";
  if (code === "EVASO") return "Evaso";
  if (code === "PRONTO_RITIRO") return "Pronto al ritiro";
  if (code === "RITIRATO") return "Ritirato";
  return o?.app_status_label || "";
};

const appStatusBadgeClass = (o) => {
  const code = String(o?.app_status || "").toUpperCase();
  if (code === "PRONTO_RITIRO") return "bg-success";
  if (code === "IN_PREPARAZIONE") return "bg-primary";
  if (code === "EVASO") return "bg-info text-dark";
  if (code === "RITIRATO") return "bg-secondary";
  return "bg-info text-dark";
};

const formatDateOnlyIT = (value) => {
  if (!value) return "";
  const raw = String(value);
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return raw;
};

const formatTimeIT = (value) => {
  if (!value) return "";
  const raw = String(value);
  const m = raw.match(/^(\d{2}):(\d{2})/);
  if (m) return `${m[1]}:${m[2]}`;
  return raw;
};

const pickupLabel = (o) => {
  if (!o?.pickup_date && !o?.pickup_time_from && !o?.pickup_time_to) return "";
  return `${formatDateOnlyIT(o.pickup_date)} — ${formatTimeIT(o.pickup_time_from)} / ${formatTimeIT(o.pickup_time_to)}`;
};

const APP_TRACKING_STEPS = [
  { value: "INVIATO", label: "Ricevuto" },
  { value: "IN_PREPARAZIONE", label: "In preparazione" },
  { value: "EVASO", label: "Evaso" },
  { value: "PRONTO_RITIRO", label: "Pronto al ritiro" },
  { value: "RITIRATO", label: "Ritirato" },
];

const trackingIndex = (o) => {
  const current = String(o?.app_status || "INVIATO").toUpperCase();
  const idx = APP_TRACKING_STEPS.findIndex((s) => s.value === current);
  return idx >= 0 ? idx : 0;
};

const notifyReadyOrder = () => {
  try {
    if (navigator?.vibrate) {
      navigator.vibrate([180, 80, 180]);
    }
  } catch {}

  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;

    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = "sine";
    osc.frequency.value = 880;
    gain.gain.value = 0.06;

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start();
    setTimeout(() => {
      try {
        osc.stop();
        ctx.close();
      } catch {}
    }, 180);
  } catch {}
};

export default function MieiOrdini() {
  const [meRole, setMeRole] = useState("");
  const [ordini, setOrdini] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("ALL");
  const [readyNotice, setReadyNotice] = useState(null);
  const previousStatusesRef = useRef({});
  const nav = useNavigate();
  const location = useLocation();
  const viewMode = new URLSearchParams(location.search).get("view");
  const fromStorico = new URLSearchParams(location.search).get("from") === "storico";
  const isLavorazioneView = viewMode === "lavorazione";
  const isAttesaView = viewMode === "attesa";

  const fetchMe = async () => {
    try {
      const token = localStorage.getItem("access");
      const res = await fetch(`${API_URL}/api/me/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const me = await res.json();
        setMeRole(me?.role || "");
      }
    } catch {}
  };

  const fetchOrdini = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access");
      const res = await fetch(`${API_URL}/api/ordini/miei/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const arr = Array.isArray(data) ? data : data.results || [];

      const previousStatuses = previousStatusesRef.current || {};
      const readyOrder = arr.find((o) => {
        const current = String(o.app_status || "").toUpperCase();
        const previous = previousStatuses[o.id];
        return current === "PRONTO_RITIRO" && previous && previous !== "PRONTO_RITIRO";
      });

      const nextStatuses = {};
      arr.forEach((o) => {
        nextStatuses[o.id] = String(o.app_status || "").toUpperCase();
      });
      previousStatusesRef.current = nextStatuses;

      if (readyOrder) {
        setReadyNotice({
          id: readyOrder.id,
          pickup: pickupLabel(readyOrder),
        });
        notifyReadyOrder();
      }

      setOrdini(arr);
    } catch (e) {
      console.error(e);
      setOrdini([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMe();
    fetchOrdini();

    const intervalId = setInterval(() => {
      fetchOrdini();
    }, 10000);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!readyNotice) return;
    const t = setTimeout(() => setReadyNotice(null), 8000);
    return () => clearTimeout(t);
  }, [readyNotice]);

  const ordiniView = useMemo(() => {
    let arr = [...ordini];
    if (isAttesaView) {
      arr = arr.filter(o => ["SUBMITTED", "INVIATO"].includes((o.status || "").toUpperCase()));
    } else if (isLavorazioneView) {
      arr = arr.filter(o => ["APPROVED"].includes((o.status || "").toUpperCase()) || (["FULFILLED", "EVASO"].includes((o.status || "").toUpperCase()) && !o.has_proforma));
    } else if (viewMode === "storico") {
      arr = arr.filter(o => ((["FULFILLED", "EVASO"].includes((o.status || "").toUpperCase()) && o.has_proforma) || ["CANCELED", "ANNULLATO"].includes((o.status || "").toUpperCase())));
    } else if (status !== "ALL") {
      arr = arr.filter(o => (o.status || "").toUpperCase() === status);
    }
    const k = q.trim().toLowerCase();
    if (k) {
      arr = arr.filter(o =>
        [o.id, o.status, o.payment_method]
          .map(v => String(v ?? "").toLowerCase())
          .some(v => v.includes(k))
      );
    }
    return arr;
  }, [ordini, q, status, isLavorazioneView, isAttesaView]);

  const creaBozza = async () => {
    try {
      const token = localStorage.getItem("access");
      const res = await fetch(`${API_URL}/api/ordini/miei/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          status: "DRAFT",
          payment_method: "RITIRO",
          items: [],
          messaggio_agente: "",
        }),
      });

      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { __text: text }; }
      if (!res.ok) throw new Error(data.detail || "Errore creazione bozza");
      nav(`/ordini/${data.id}/edit`);
    } catch (e) {
      alert(e.message || "Errore creazione bozza");
    }
  };

  const inviaOrdine = async (ordId) => {
    if (!window.confirm("Inviare l'ordine? Dopo l'invio non sarà più modificabile.")) return;
    try {
      const token = localStorage.getItem("access");
      const res = await fetch(`${API_URL}/api/ordini/${ordId}/invia/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { __text: text }; }
      if (!res.ok) throw new Error(data.detail || "Errore invio ordine");
      fetchOrdini();
    } catch (e) {
      alert(e.message || "Errore invio");
    }
  };

  const showCommissionColumn = isAgentRole(meRole);

  return (
    <div className="container mt-4">
      <TornaAlMenu to="/" />
      {fromStorico && (
        <button className="btn btn-outline-secondary mb-3 ms-2" onClick={() => nav("/ordini/storico")}>
          ← Torna allo storico ordini
        </button>
      )}
      {readyNotice && (
        <div className="alert alert-success shadow-sm border-0 rounded-4 mb-3">
          <div className="fw-bold">🔔 Il tuo ordine #{readyNotice.id} è pronto al ritiro</div>
          <div className="small">
            {readyNotice.pickup ? `Passa a ritirarlo: ${readyNotice.pickup}` : "Puoi passare a ritirarlo."}
          </div>
        </div>
      )}

      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
          <h2 className="m-0">{isAttesaView ? "🔴 Ordini in attesa" : isLavorazioneView ? "🟡 Ordini in lavorazione" : "🧾 I miei ordini"}</h2>
        <div className="d-flex gap-2">
            {!isLavorazioneView && (
              <button className="btn btn-success" onClick={creaBozza}>➕ Nuova bozza</button>
            )}
          <button className="btn btn-outline-primary" onClick={fetchOrdini}>🔄 Aggiorna</button>
        </div>
      </div>

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="row g-2 align-items-end">
            <div className="col-md-4">
              <label className="form-label mb-1">Cerca</label>
              <input
                className="form-control"
                placeholder="🔎 ID, stato, metodo pagamento…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
              {!isLavorazioneView && (
                <>
            <div className="col-md-3">
              <label className="form-label mb-1">Stato</label>
              <select
                className="form-select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="ALL">Tutti</option>
                <option value="DRAFT">Bozza</option>
                <option value="SUBMITTED">Inviato</option>
                <option value="APPROVED">Approvato</option>
                <option value="FULFILLED">Evaso</option>
                <option value="CANCELED">Annullato</option>
              </select>
            </div>
            <div className="col-md-3 text-md-end">
              <button
                className="btn btn-outline-secondary mt-3 mt-md-0"
                onClick={() => { setQ(""); setStatus("ALL"); }}>
                Reset
              </button>
            </div>
                </>
              )}
          </div>
        </div>
      </div>

      {loading ? (
        <p className="text-muted">Caricamento…</p>
      ) : ordiniView.length === 0 ? (
        <div className="alert alert-info">{isAttesaView ? "Nessun ordine in attesa." : isLavorazioneView ? "Nessun ordine in lavorazione." : "Nessun ordine."}</div>
      ) : (
        <div className="table-responsive">
          <table className="table align-middle">
            <thead>
              <tr>
                <th>ID</th>
                <th>Data</th>
                <th>Stato</th>
                <th>Totale</th>
                {showCommissionColumn && <th>Provvigione</th>}
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {ordiniView.map(o => (
                <tr key={o.id}>
                  <td>#{o.id}</td>
                  <td>{o.date}</td>
                    <td>
                      {String(o.app_status || "").toUpperCase() === "PRONTO_RITIRO" && (
                        <div className="alert alert-success py-2 px-3 mb-2 rounded-4 border-0">
                          <div className="fw-bold">🟢 Ordine pronto al ritiro</div>
                          {pickupLabel(o) && (
                            <div className="small">Passa a ritirarlo: {pickupLabel(o)}</div>
                          )}
                        </div>
                      )}

                      {o.app_status ? (
                        <span className={`badge ${appStatusBadgeClass(o)}`}>
                          {appStatusLabel(o)}
                        </span>
                      ) : (
                        <span className={`badge ${
                          o.status === "DRAFT" ? "bg-warning text-dark" :
                          o.status === "SUBMITTED" ? "bg-info text-dark" :
                          o.status === "APPROVED" ? "bg-primary" :
                          o.status === "FULFILLED" ? "bg-success" : "bg-secondary"
                        }`}>
                          {statoLabel(o.status)}
                        </span>
                      )}

                      {pickupLabel(o) && String(o.app_status || "").toUpperCase() !== "PRONTO_RITIRO" && (
                        <div className="small text-muted mt-1">
                          Ritiro: {pickupLabel(o)}
                        </div>
                      )}

                      {o.app_status && (
                        <div className="mt-3">
                          <div className="small text-muted mb-2">Avanzamento ordine</div>
                          <div className="d-flex flex-wrap align-items-center gap-2">
                            {APP_TRACKING_STEPS.map((step, idx) => {
                              const done = idx <= trackingIndex(o);
                              const active = idx === trackingIndex(o);
                              return (
                                <div key={step.value} className="d-flex align-items-center gap-2">
                                  <span
                                    className={`badge rounded-pill ${done ? "bg-success" : "bg-light text-dark border"}`}
                                    style={{ minWidth: 30 }}
                                  >
                                    {done ? "✓" : "○"}
                                  </span>
                                  <span className={active ? "fw-bold text-success" : done ? "text-success" : "text-muted"}>
                                    {step.label}
                                  </span>
                                  {idx < APP_TRACKING_STEPS.length - 1 && (
                                    <span className={done ? "text-success" : "text-muted"}>→</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </td>
                  <td>€ {Number(o.total || 0).toFixed(2)}</td>

                  {showCommissionColumn && (
                    <td>
                      {isOrderFromAgent(o)
                        ? `€ ${Number(o.commission_amount || 0).toFixed(2)}`
                        : "—"}
                    </td>
                  )}

                  <td className="d-flex gap-2">
                    {o.status === "DRAFT" && (
                      <>
                        <button className="btn btn-sm btn-outline-primary" onClick={() => nav(`/ordini/${o.id}/edit`)}>
                          Modifica
                        </button>
                        <button className="btn btn-sm btn-outline-success" onClick={() => inviaOrdine(o.id)}>
                          Invia
                        </button>
                      </>
                    )}
                    <button className="btn btn-sm btn-outline-dark" onClick={() => nav(`/ordini/${o.id}`)}>
                      Apri
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
